/**
 * El modo claro/oscuro del panel y el editor.
 *
 * Lo que se GUARDA es la preferencia —claro, oscuro o automático— y lo que se
 * PINTA es el tema que sale de ella. Son dos cosas porque «automático» no es un
 * tema: con él, el atributo de `<html>` cambia solo cuando el sistema pasa de
 * día a noche, sin que nadie toque el menú.
 *
 * Misma mecánica que el acento (ver `accent.ts`): cookie compartida en
 * `.veltica.com` para cruzar del panel al editor, copia en `localStorage`, y un
 * evento para que los dos sitios que lo miran en una misma página —el menú de
 * la cuenta y la aplicación que pinta— lean el valor nuevo.
 *
 * Lo que NO hace es aplicarlo: el color de la barra de Safari es distinto en
 * cada aplicación, así que `applyTheme` recibe los dos tonos de quien llama.
 */
export const THEME_PREFERENCES = ['light', 'dark', 'system'];
/**
 * Quien todavía no eligió va con su sistema. Antes era claro, pero todo el que
 * abrió la app alguna vez tiene la cookie escrita, así que esto sólo cambia a
 * quien llega por primera vez.
 */
export const DEFAULT_THEME_PREFERENCE = 'system';
const STORAGE_KEY = 'veltica_theme';
const COOKIE = 'veltica_theme';
/* Las claves de antes de compartir la cookie. Sólo se leen. */
const LEGACY_KEYS = ['veltica_dash_theme_v2', 'veltica_editor_theme'];
const CHANGE_EVENT = 'veltica:theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';
function isPreference(value) {
    return value === 'light' || value === 'dark' || value === 'system';
}
function cookiePreference() {
    const entry = document.cookie
        .split('; ')
        .find((part) => part.startsWith(`${COOKIE}=`));
    const value = entry?.slice(COOKIE.length + 1);
    return isPreference(value) ? value : null;
}
/** La elegida, o la de la casa. La cookie manda: es la que cruza subdominios. */
export function readThemePreference() {
    const shared = cookiePreference();
    if (shared)
        return shared;
    try {
        for (const key of [STORAGE_KEY, ...LEGACY_KEYS]) {
            const stored = localStorage.getItem(key);
            if (isPreference(stored))
                return stored;
        }
    }
    catch {
        /* En una ventana privada no hay almacenamiento; se queda la de la casa. */
    }
    return DEFAULT_THEME_PREFERENCE;
}
function systemTheme() {
    return window.matchMedia?.(DARK_QUERY).matches ? 'dark' : 'light';
}
export function resolveTheme(preference) {
    return preference === 'system' ? systemTheme() : preference;
}
/** El tema que toca pintar ahora mismo. */
export function readTheme() {
    return resolveTheme(readThemePreference());
}
export function persistThemePreference(preference) {
    try {
        localStorage.setItem(STORAGE_KEY, preference);
    }
    catch {
        /* La cookie sigue cubriendo la navegación normal entre subdominios. */
    }
    const domain = location.hostname === 'veltica.com' || location.hostname.endsWith('.veltica.com')
        ? '; Domain=.veltica.com'
        : '';
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE}=${preference}; Path=/; Max-Age=31536000; SameSite=Lax${domain}${secure}`;
    window.dispatchEvent(new Event(CHANGE_EVENT));
}
/**
 * Avisa cuando cambia la preferencia —aquí o en otra pestaña— y también cuando
 * cambia el sistema, que con «automático» cambia el tema sin cambiar nada
 * guardado. Avisar de más no cuesta: quien escucha vuelve a leer y, si sale lo
 * mismo, React no repinta.
 */
export function subscribeTheme(listener) {
    const media = window.matchMedia?.(DARK_QUERY);
    window.addEventListener(CHANGE_EVENT, listener);
    window.addEventListener('storage', listener);
    media?.addEventListener('change', listener);
    return () => {
        window.removeEventListener(CHANGE_EVENT, listener);
        window.removeEventListener('storage', listener);
        media?.removeEventListener('change', listener);
    };
}
/**
 * Lo pinta: el atributo que miran las hojas de estilo y el color de la barra.
 *
 * Ese color tiene que ser el del FONDO del marco, no el de la tarjeta: la barra
 * se pega al borde de la pantalla, que es donde está el fondo.
 */
export function applyTheme(theme, barColors) {
    document.documentElement.setAttribute('data-theme', theme);
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', barColors[theme]);
}
//# sourceMappingURL=theme.js.map